import { resolveNrcProximity } from './reportSourceEvidence';
import type { SiteEntity } from '../types/site';
import type { SiteEvidenceDocument } from '../types/siteEvidence';

function finiteOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function parcelAcres(site: SiteEntity, sed: SiteEvidenceDocument): number | null {
  return finiteOrNull(sed.siteProfile?.data?.acres) ?? finiteOrNull(site.siteArea);
}

function isUnmappedFloodZone(zone: string | undefined): boolean {
  if (!zone || !zone.trim()) return true;
  return /^(unknown|unmapped|not mapped|n\/a|none)$/i.test(zone.trim());
}

/**
 * Deterministic Executive Summary — BTM Permitting Outlook from SED.
 * Used as the environmental report fallback and as the contract for
 * what the AI executiveSummary must synthesize.
 */
export function buildEnvironmentalBtmPermittingOutlookSummary(
  site: SiteEntity,
  sed: SiteEvidenceDocument,
): string {
  const strengths: string[] = [];
  const risks: string[] = [];

  const sdc = sed.seismic?.data?.designCategory?.trim();
  if (sdc) {
    strengths.push(`low seismic exposure (Seismic Design Category ${sdc})`);
  }

  const water = sed.waterStress?.data;
  const bws = finiteOrNull(water?.bwsScore);
  const bwsLabel = water?.bwsLabel?.trim();
  if (bws != null && water?.stressLevel === 'Low') {
    strengths.push(`low baseline water stress (WRI ${bws}/5; regional screening only)`);
  } else if (water?.stressLevel === 'High' || water?.stressLevel === 'Moderate') {
    risks.push(`${bws == null ? water.stressLevel : bwsLabel || water.stressLevel} baseline water stress${bws == null ? ' (complete-study score unavailable)' : ` (WRI ${bws}/5)`}; verify local water supply and allocation`);
  } else if (water) {
    risks.push('baseline water stress is not established for the complete study; verify local water supply and allocation');
  }

  const nuclear = sed.nuclearInfra?.data;
  const reactorDist = nuclear ? resolveNrcProximity(nuclear).distanceKm : null;
  if (nuclear?.nearestReactorName && reactorDist != null) {
    strengths.push(
      `proximity to ${nuclear.nearestReactorName} (${reactorDist.toFixed(1)} km)`,
    );
  }

  const fz = sed.flood?.data?.floodZone?.trim();
  if (isUnmappedFloodZone(fz)) {
    risks.push('unmapped FEMA flood zone on the parcel');
  }

  const acres = parcelAcres(site, sed);
  const wetlandAcres = finiteOrNull(sed.environmental?.data?.wetlandAcres);
  if (sed.environmental?.data?.wetlandAcreageBasis === 'source-feature-total') {
    risks.push('mapped wetlands occur in the surrounding source query; within-parcel acreage and jurisdiction are unresolved');
  } else if (acres != null && wetlandAcres != null && wetlandAcres > acres * 1.25) {
    risks.push(
      `wetlands data discrepancy (${Math.round(wetlandAcres).toLocaleString('en-US')} acres reported on a ${acres.toFixed(1)}-acre parcel)`,
    );
  } else if (sed.environmental?.data?.hasWetlands && wetlandAcres != null && wetlandAcres > 0 && acres != null) {
    // 1-decimal to match the site-details + extended-data readers (both
    // `.toFixed(1)`); a prior `Math.round` here surfaced "66 acres" against
    // their "65.6 acres" for the same wetland footprint.
    risks.push(`${wetlandAcres.toFixed(1)} acres of mapped wetlands reported for the ${Math.round(acres)}-acre parcel`);
  }

  const tribal = sed.siteResearch?.data?.tribalLands;
  if (tribal?.consultationRequired || (tribal?.nearbyTribes?.length ?? 0) > 0) {
    const tribes = tribal?.nearbyTribes?.filter(Boolean).slice(0, 3).join(', ');
    risks.push(tribes ? `tribal consultation applicability unresolved (${tribes})` : 'tribal consultation applicability unresolved');
  }

  for (const check of sed.crossValidation?.checks ?? []) {
    if (!check.passed && check.severity !== 'info' && typeof check.message === 'string') {
      const msg = check.message.trim().replace(/\.$/, '');
      if (/wetland|flood|tribal|discrep|data integrity/i.test(msg)) {
        if (!risks.some(r => r.toLowerCase().includes(msg.slice(0, 24).toLowerCase()))) {
          risks.push(msg);
        }
      }
    }
  }

  const parts: string[] = [];
  parts.push(`${site.name} — BTM permitting outlook synthesized from site evidence.`);

  if (strengths.length > 0) {
    parts.push(`Favourable indicators include ${strengths.join(', ')}.`);
  }

  if (fz && !isUnmappedFloodZone(fz)) {
    parts.push(`FEMA flood zone ${fz}${sed.flood?.data?.sfha ? ' (Special Flood Hazard Area)' : ''}.`);
  }

  if (risks.length > 0) {
    parts.push(`Key diligence items: ${risks.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join('; ')}.`);
  }

  if (strengths.length === 0 && risks.length === 0) {
    parts.push('Environmental screening data is limited — refresh narrative when additional evidence is available.');
  }

  return parts.join(' ');
}
